import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  FileText,
  Home as HomeIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "l4_admin_token";
const EMPTY_JOB = {
  title: "",
  location: "",
  contract: "CDI",
  salary: "",
  domain: "",
  description: "",
  tags: "",
  active: true,
};

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [logging, setLogging] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [requests, setRequests] = useState([]);
  const [editing, setEditing] = useState(null); // job object or null
  const [showForm, setShowForm] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [j, a, r] = await Promise.all([
        axios.get(`${API}/admin/jobs`, auth),
        axios.get(`${API}/admin/applications`, auth),
        axios.get(`${API}/admin/recruit-requests`, auth),
      ]);
      setJobs(j.data);
      setApplications(a.data);
      setRequests(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expirée, reconnectez-vous.");
        logout();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) loadAll();
  }, [token, loadAll]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLogging(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Connecté à l'espace admin.");
    } catch (err) {
      toast.error("Mot de passe incorrect.");
    } finally {
      setLogging(false);
    }
  };

  const openNew = () => {
    setEditing({ ...EMPTY_JOB });
    setShowForm(true);
  };
  const openEdit = (job) => {
    setEditing({ ...job, tags: (job.tags || []).join(", ") });
    setShowForm(true);
  };

  const saveJob = async (e) => {
    e.preventDefault();
    const payload = {
      title: editing.title,
      location: editing.location,
      contract: editing.contract || "CDI",
      salary: editing.salary,
      domain: editing.domain,
      description: editing.description,
      tags: editing.tags
        ? editing.tags.split(",").map((t) => t.trim()).filter(Boolean)
        : [],
      active: editing.active,
    };
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/jobs/${editing.id}`, payload, auth);
        toast.success("Offre mise à jour.");
      } else {
        await axios.post(`${API}/admin/jobs`, payload, auth);
        toast.success("Offre créée.");
      }
      setShowForm(false);
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement.");
    }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Supprimer l'offre "${job.title}" ?`)) return;
    try {
      await axios.delete(`${API}/admin/jobs/${job.id}`, auth);
      toast.success("Offre supprimée.");
      loadAll();
    } catch {
      toast.error("Erreur lors de la suppression.");
    }
  };

  const toggleActive = async (job) => {
    try {
      await axios.put(
        `${API}/admin/jobs/${job.id}`,
        { ...job, active: !job.active },
        auth
      );
      loadAll();
    } catch {
      toast.error("Erreur.");
    }
  };

  // ---------- Login screen ----------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a1626] flex items-center justify-center px-6">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.03] p-8"
        >
          <div className="flex justify-center">
            <Logo />
          </div>
          <div className="mt-6 flex items-center justify-center gap-2 text-slate-300">
            <Lock size={18} className="text-[#F5941E]" />
            <h1 className="text-lg font-bold text-white">Espace administrateur</h1>
          </div>
          <p className="mt-2 text-center text-sm text-slate-400">
            Connectez-vous pour gérer les offres et consulter les demandes.
          </p>
          <div className="mt-6 space-y-2">
            <Label className="text-slate-300">Mot de passe</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-white/5 border-white/10 text-white focus-visible:ring-[#F5941E]"
            />
          </div>
          <button
            type="submit"
            disabled={logging}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F5941E] hover:bg-[#e8850e] px-6 py-3 font-semibold text-white transition-colors disabled:opacity-60"
          >
            {logging && <Loader2 size={18} className="animate-spin" />}
            Se connecter
          </button>
          <Link
            to="/"
            className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-[#F5941E]"
          >
            <HomeIcon size={14} /> Retour au site
          </Link>
        </form>
      </div>
    );
  }

  // ---------- Dashboard ----------
  const TABS = [
    { key: "jobs", label: `Offres (${jobs.length})` },
    { key: "apps", label: `Candidatures (${applications.length})` },
    { key: "reqs", label: `Demandes (${requests.length})` },
  ];

  return (
    <div className="min-h-screen bg-[#0a1626]">
      <header className="border-b border-white/10 bg-[#0a1626]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden sm:inline text-sm font-semibold text-slate-300">
              Espace administrateur
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#F5941E]"
            >
              <HomeIcon size={16} /> Voir le site
            </Link>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white hover:bg-white/10"
            >
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Tabs */}
        <div className="flex flex-wrap gap-3">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                tab === t.key
                  ? "bg-[#F5941E] text-white"
                  : "border border-white/15 bg-white/5 text-slate-300 hover:bg-white/10"
              }`}
            >
              {t.label}
            </button>
          ))}
          {tab === "jobs" && (
            <button
              onClick={openNew}
              className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#F5941E] hover:bg-[#e8850e] px-5 py-2 text-sm font-semibold text-white"
            >
              <Plus size={16} /> Nouvelle offre
            </button>
          )}
        </div>

        {/* Jobs */}
        {tab === "jobs" && (
          <div className="mt-8 space-y-3">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-white">{job.title}</h3>
                    <span className="rounded-full bg-[#F5941E]/15 px-2.5 py-0.5 text-xs text-[#F5941E]">
                      {job.domain}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-400">
                    {job.location} · {job.contract} · {job.salary}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-xs text-slate-400">
                    <Switch
                      checked={job.active}
                      onCheckedChange={() => toggleActive(job)}
                    />
                    {job.active ? "En ligne" : "Masquée"}
                  </label>
                  <button
                    onClick={() => openEdit(job)}
                    className="p-2 rounded-lg text-slate-300 hover:bg-white/10 hover:text-[#F5941E]"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => deleteJob(job)}
                    className="p-2 rounded-lg text-slate-300 hover:bg-white/10 hover:text-red-400"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
            {jobs.length === 0 && (
              <p className="text-slate-400">Aucune offre pour le moment.</p>
            )}
          </div>
        )}

        {/* Applications */}
        {tab === "apps" && (
          <div className="mt-8 space-y-3">
            {applications.map((a) => (
              <div
                key={a.id}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold text-white">{a.name}</h3>
                  <span className="text-xs text-slate-500">
                    {new Date(a.created_at).toLocaleString("fr-FR")}
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-300">
                  {a.email} · {a.phone || "—"}
                </p>
                <p className="mt-1 text-sm text-slate-400">
                  Poste : {a.company || "—"} · Offre :{" "}
                  {a.job_title || "Candidature spontanée"}
                </p>
                {a.message && (
                  <p className="mt-2 text-sm text-slate-400">{a.message}</p>
                )}
                {a.cv_filename && (
                  <p className="mt-2 inline-flex items-center gap-2 text-xs text-[#F5941E]">
                    <FileText size={14} /> CV joint : {a.cv_filename} (envoyé par
                    email)
                  </p>
                )}
              </div>
            ))}
            {applications.length === 0 && (
              <p className="text-slate-400">Aucune candidature reçue.</p>
            )}
          </div>
        )}

        {/* Requests */}
        {tab === "reqs" && (
          <div className="mt-8 space-y-3">
            {requests.map((r) => (
              <div
                key={r.id}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold text-white">{r.name}</h3>
                  <span className="text-xs text-slate-500">
                    {new Date(r.created_at).toLocaleString("fr-FR")}
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-300">
                  {r.email} · {r.phone || "—"}
                </p>
                <p className="mt-1 text-sm text-slate-400">
                  Entreprise : {r.company || "—"}
                </p>
                {r.message && (
                  <p className="mt-2 text-sm text-slate-400">{r.message}</p>
                )}
              </div>
            ))}
            {requests.length === 0 && (
              <p className="text-slate-400">Aucune demande reçue.</p>
            )}
          </div>
        )}
      </main>

      {/* Job form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg bg-[#0f1e33] border border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editing?.id ? "Modifier l'offre" : "Nouvelle offre"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveJob} className="space-y-4">
              <FieldA label="Intitulé du poste *">
                <Input
                  value={editing.title}
                  onChange={(e) =>
                    setEditing({ ...editing, title: e.target.value })
                  }
                  required
                  className="bg-white/5 border-white/10 text-white focus-visible:ring-[#F5941E]"
                />
              </FieldA>
              <div className="grid grid-cols-2 gap-4">
                <FieldA label="Localisation *">
                  <Input
                    value={editing.location}
                    onChange={(e) =>
                      setEditing({ ...editing, location: e.target.value })
                    }
                    required
                    placeholder="Nantes (44)"
                    className="bg-white/5 border-white/10 text-white focus-visible:ring-[#F5941E]"
                  />
                </FieldA>
                <FieldA label="Contrat">
                  <Input
                    value={editing.contract}
                    onChange={(e) =>
                      setEditing({ ...editing, contract: e.target.value })
                    }
                    placeholder="CDI"
                    className="bg-white/5 border-white/10 text-white focus-visible:ring-[#F5941E]"
                  />
                </FieldA>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FieldA label="Salaire *">
                  <Input
                    value={editing.salary}
                    onChange={(e) =>
                      setEditing({ ...editing, salary: e.target.value })
                    }
                    required
                    placeholder="40 - 50 K€"
                    className="bg-white/5 border-white/10 text-white focus-visible:ring-[#F5941E]"
                  />
                </FieldA>
                <FieldA label="Domaine *">
                  <Input
                    value={editing.domain}
                    onChange={(e) =>
                      setEditing({ ...editing, domain: e.target.value })
                    }
                    required
                    placeholder="Maintenance"
                    className="bg-white/5 border-white/10 text-white focus-visible:ring-[#F5941E]"
                  />
                </FieldA>
              </div>
              <FieldA label="Description *">
                <Textarea
                  value={editing.description}
                  onChange={(e) =>
                    setEditing({ ...editing, description: e.target.value })
                  }
                  required
                  rows={4}
                  className="bg-white/5 border-white/10 text-white focus-visible:ring-[#F5941E] resize-none"
                />
              </FieldA>
              <FieldA label="Compétences (séparées par des virgules)">
                <Input
                  value={editing.tags}
                  onChange={(e) =>
                    setEditing({ ...editing, tags: e.target.value })
                  }
                  placeholder="GMAO, Management, Préventif"
                  className="bg-white/5 border-white/10 text-white focus-visible:ring-[#F5941E]"
                />
              </FieldA>
              <label className="flex items-center gap-3 text-sm text-slate-300">
                <Switch
                  checked={editing.active}
                  onCheckedChange={(v) =>
                    setEditing({ ...editing, active: v })
                  }
                />
                Offre visible sur le site
              </label>
              <button
                type="submit"
                className="w-full rounded-full bg-[#F5941E] hover:bg-[#e8850e] px-6 py-3 font-semibold text-white transition-colors"
              >
                {editing.id ? "Enregistrer les modifications" : "Créer l'offre"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldA({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-300">{label}</Label>
      {children}
    </div>
  );
}
